import React from "react";

import AppDatePicker_New from "../AppDatePicker_New/AppDatePicker_New";
import "./AppRowInput_New.scss";

export interface AppRowInputRow {
  id: string;
  specimenCollectionDate: Date | null;
  specimenId: string;
  details: string;
  titer: string;
}

export interface AppRowInputSection {
  id: string;
  title: string;
  rows: AppRowInputRow[];
}

export interface AppRowInputLabels {
  addRow: string;
  collectionDate: string;
  specimenId: string;
  details: string;
  titer: string;
  removeRow: string;
  empty: string;
}

export interface AppRowInputProps {
  title: string;
  name: string;
  sections: AppRowInputSection[];
  onChange: (sections: AppRowInputSection[]) => void;
  labels?: Partial<AppRowInputLabels>;
  datePlaceholder?: string;
  dateFormat?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
  disabled?: boolean;
  createRow?: (section: AppRowInputSection) => AppRowInputRow;
}

const defaultLabels: AppRowInputLabels = {
  addRow: "Add row",
  collectionDate: "Specimen Collection Date",
  specimenId: "Specimen ID",
  details: "Details",
  titer: "Titer",
  removeRow: "Remove row",
  empty: "No results recorded.",
};

let nextRowId = 0;

const makeRow = (): AppRowInputRow => ({
  id: `test-result-row-${Date.now()}-${nextRowId++}`,
  specimenCollectionDate: null,
  specimenId: "",
  details: "",
  titer: "",
});

const AppRowInput_New: React.FC<AppRowInputProps> = ({
  title,
  name,
  sections,
  onChange,
  labels: labelOverrides,
  datePlaceholder = "MM/DD/YYYY",
  dateFormat = "MM/dd/yyyy",
  minDate,
  maxDate,
  disabled = false,
  createRow,
}) => {
  const labels = { ...defaultLabels, ...labelOverrides };

  const updateSection = (
    sectionIndex: number,
    updater: (section: AppRowInputSection) => AppRowInputSection
  ) => {
    onChange(
      sections.map((section, index) =>
        index === sectionIndex ? updater(section) : section
      )
    );
  };

  const updateRow = <K extends keyof Omit<AppRowInputRow, "id">>(
    sectionIndex: number,
    rowIndex: number,
    field: K,
    value: AppRowInputRow[K]
  ) => {
    updateSection(sectionIndex, (section) => ({
      ...section,
      rows: section.rows.map((row, index) =>
        index === rowIndex ? { ...row, [field]: value } : row
      ),
    }));
  };

  return (
    <div className="app-row-input">
      <h2 className="app-row-input__title">{title}</h2>

      <div className="app-row-input__sections">
        {sections.map((section, sectionIndex) => (
          <section className="app-row-input__section" key={section.id}>
            <header className="app-row-input__section-header">
              <h3>{section.title}</h3>
              <button
                type="button"
                className="app-row-input__add"
                disabled={disabled}
                onClick={() =>
                  updateSection(sectionIndex, (current) => ({
                    ...current,
                    rows: [
                      ...current.rows,
                      createRow ? createRow(current) : makeRow(),
                    ],
                  }))
                }
              >
                <span aria-hidden="true">+</span> {labels.addRow}
              </button>
            </header>

            {section.rows.length ? (
              <div className="app-row-input__table" role="table">
                <div className="app-row-input__head" role="row">
                  <span role="columnheader">{labels.collectionDate}</span>
                  <span role="columnheader">{labels.specimenId}</span>
                  <span role="columnheader">{labels.details}</span>
                  <span role="columnheader">{labels.titer}</span>
                  <span role="columnheader" aria-label={labels.removeRow} />
                </div>

                {section.rows.map((row, rowIndex) => (
                  <div className="app-row-input__row" role="row" key={row.id}>
                    <div
                      className="app-row-input__cell app-row-input__date"
                      role="cell"
                      data-label={labels.collectionDate}
                    >
                      <AppDatePicker_New
                        name={`${name}.${section.id}.${row.id}.specimenCollectionDate`}
                        placeholder={datePlaceholder}
                        dateFormat={dateFormat}
                        setDate={row.specimenCollectionDate}
                        setMinDate={minDate}
                        setMaxDate={maxDate}
                        disabled={disabled}
                        //showFormatMask={false}
                        onCustomChange={(date) =>
                          updateRow(
                            sectionIndex,
                            rowIndex,
                            "specimenCollectionDate",
                            date
                          )
                        }
                      />
                    </div>

                    {(["specimenId", "details", "titer"] as const).map(
                      (field) => (
                        <div
                          className="app-row-input__cell"
                          role="cell"
                          data-label={labels[field]}
                          key={field}
                        >
                          <input
                            type="text"
                            aria-label={labels[field]}
                            value={row[field]}
                            disabled={disabled}
                            onChange={(event) =>
                              updateRow(
                                sectionIndex,
                                rowIndex,
                                field,
                                event.target.value
                              )
                            }
                          />
                        </div>
                      )
                    )}

                    <div className="app-row-input__cell app-row-input__remove-cell" role="cell">
                      <button
                        type="button"
                        className="app-row-input__remove"
                        aria-label={`${labels.removeRow} ${rowIndex + 1}`}
                        title={labels.removeRow}
                        disabled={disabled}
                        onClick={() =>
                          updateSection(sectionIndex, (current) => ({
                            ...current,
                            rows: current.rows.filter((_, index) => index !== rowIndex),
                          }))
                        }
                      >
                        <span aria-hidden="true">&#128465;</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="app-row-input__empty">{labels.empty}</p>
            )}
          </section>
        ))}
      </div>
    </div>
  );
};

export default AppRowInput_New;
